/**
 * **Is the interviewing going where the ratings said it should?**
 *
 * The one block on this tab that is made of the DISAGREEMENT between its two halves rather than in
 * spite of it (Theresa, Oct 19 2026): the hours above are what the priority ratings bought, the
 * index below is what the team is actually doing, and a cluster running far more of the
 * interviewing than its ratings buy is capacity not reaching the high-priority roles somewhere
 * else. The rule and every threshold live in `utils/priorityAlignment.ts`.
 *
 * ⚠️ IT IS A DISTRIBUTION, NOT A VERDICT ON A TEAM. Both figures are shares of the company's own
 * total, so a cluster can rise because another went quiet — and "above its share" is not "working
 * too hard". The caption says that, because the sentence a reader supplies for themselves here is
 * the wrong one.
 *
 * ⚠️ Tone is deliberately NEUTRAL — amber for the one cluster furthest above, grey for everything
 * else. Not red: red on this tab is the cluster index's own RAG scale, and a second red meaning
 * something different one section up is two colour systems on one screen.
 */
import type { CheckInData } from '../../../types/taCheckin';
import type { EffortModel } from '../../../types/rolePrioritisation';
import type { ClusterStats } from '../../../utils/rolePrioritisation';
import { interviewsPerWeek } from '../../../utils/taCheckinIndex';
import { priorityAlignment, type AlignmentRow } from '../../../utils/priorityAlignment';

const pct = (v: number | null) => (v == null ? '—' : `${Math.round(v * 100)}%`);

export function PriorityAlignment({
  data,
  stats,
  model,
}: {
  data?: CheckInData | null;
  stats: ClusterStats[];
  model: EffortModel;
}) {
  if (!data || !stats.length) {
    return (
      <p
        data-testid="priority-alignment-unavailable"
        className="mt-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-xs text-gray-500"
      >
        Interview counts could not be read, so the check against the priority ratings is not shown.
        The hours above are unaffected — they come from a different source.
      </p>
    );
  }

  const interviews = Object.fromEntries(
    stats.map((c) => [c.key, interviewsPerWeek(data, c.key)])
  ) as Record<string, number | null>;
  const a = priorityAlignment(stats, model, interviews);
  /*
   * ⚠️ TWO REASONS A CLUSTER HAS NO VERDICT, and they are not the same fact (CodeRabbit, PR #637).
   * Its interview count could not be READ at all — a missing row on the sheet, which somebody can
   * fix — or it was read and was too QUIET to judge. Telling a lead their cluster "had too few
   * interviews" when the figure was never read gives them the wrong thing to go and look at.
   */
  const unreadable = a.unreadable;
  const tooQuiet = a.rows
    .filter((r) => r.verdict === 'unknown' && !unreadable.includes(r.cluster))
    .map((r) => r.cluster);
  // ⚠️ `worst` only ever tracks the clusters ABOVE their share, so "nothing is off" has to check
  // the other direction too — a cluster materially BELOW its share is just as much a drift.
  const below = a.rows.filter((r) => r.verdict === 'below').map((r) => r.cluster);
  const caveats = [
    unreadable.length ? `${unreadable.join(' and ')} could not be read` : '',
    tooQuiet.length ? `${tooQuiet.join(' and ')} had too few interviews to judge` : '',
  ].filter(Boolean);

  return (
    <div
      data-testid="priority-alignment"
      className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-gray-100 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-gray-900">
          Is the interviewing following the ratings?
        </h3>
        <span className="text-xs text-gray-500">
          {a.companyInterviews} interviews last week across the clusters
        </span>
      </div>

      <div className="px-4 py-3">
        <p data-testid="alignment-headline" className="text-sm leading-6 text-gray-900">
          {!a.judgeable ? (
            /*
              ⚠️ THREE REASONS, AND THEY SEND A READER TO DIFFERENT PLACES (CodeRabbit, PR #637).
              This branch used to say "too few interviews" whatever the cause — including when the
              real problem was a cluster whose count was never read, which is a missing sheet row
              somebody can go and fix rather than a quiet week to wait out.
            */
            a.notJudgeable === 'one-cluster' ? (
              <>
                Only {a.rows.length - a.unreadable.length} cluster&apos;s interviews could be read,
                so there is nothing to compare it against
                {a.unreadable.length ? <> — {a.unreadable.join(' and ')} {a.unreadable.length === 1 ? 'is' : 'are'} missing from the sheet</> : null}.
              </>
            ) : a.notJudgeable === 'no-interviewing' ? (
              <>No open role here is rated high enough to buy any interviewing at all.</>
            ) : (
              <>
                Too few interviews last week to say — a share of {a.companyInterviews} is one or two
                bookings moving.
              </>
            )
          ) : a.worst ? (
            <>
              <b className="font-semibold">{a.worst.cluster}</b> is running{' '}
              <b className="font-semibold text-amber-700">{pct(a.worst.actualShare)}</b> of the
              interviewing while its ratings buy{' '}
              <b className="font-semibold">{pct(a.worst.expectedShare)}</b> of it
              {a.worst.cRoles > 0 ? (
                <>
                  {' '}
                  — it carries <b className="font-semibold">{a.worst.cRoles}</b>{' '}
                  {a.worst.cRoles === 1 ? 'role' : 'roles'} rated C, which buy no interviewing at
                  all
                </>
              ) : null}
              {/*
                ⚠️ THE CAVEAT BELONGS HERE TOO (CodeRabbit, PR #637). This branch used to present
                the shares with no mention that a cluster had been left out of both of them — the
                figure is only a share of the clusters that could be read, and a reader comparing
                it against the whole company would be reading it wrong.
              */}
              {caveats.length ? <>, although {caveats.join('; ')}</> : null}.
            </>
          ) : below.length ? (
            <>
              <b className="font-semibold">{below.join(' and ')}</b>{' '}
              {below.length === 1 ? 'is' : 'are'} running less of the interviewing than{' '}
              {below.length === 1 ? 'its' : 'their'} ratings buy
              {caveats.length ? `, and ${caveats.join('; ')}` : ''}.
            </>
          ) : caveats.length ? (
            /*
              ⚠️ "No cluster is off its share" is a claim about EVERY cluster, and it must not be
              made while one of them is unjudged. Naming the reason is the other half: an
              unreadable cluster is a missing sheet row somebody can go and fix.
            */
            <>
              Nothing is off its share among the clusters that can be judged —{' '}
              {caveats.join('; ')}.
            </>
          ) : (
            <>Interviewing is following the ratings — no cluster is materially off its share.</>
          )}
        </p>

        <div className="mt-3 space-y-1.5">
          {a.rows.map((row) => (
            <Row key={row.cluster} row={row} worst={a.worst?.cluster === row.cluster} />
          ))}
        </div>

        <p className="mt-3 text-xs leading-relaxed text-gray-500">
          A share of the company&apos;s own interviewing, not a workload: a cluster can rise because
          another went quiet. <b>Bought</b> is what each cluster&apos;s open roles pay for at the
          agreed hours per rating — AAA and A carry full interviewing, B is shared, and C buys none.{' '}
          <b>Ran</b> is what actually happened last week. Where they part company, the week went
          somewhere the ratings did not ask for.
        </p>
      </div>
    </div>
  );
}

function Row({ row, worst }: { row: AlignmentRow; worst: boolean }) {
  const label =
    row.verdict === 'above'
      ? 'more than its ratings buy'
      : row.verdict === 'below'
        ? 'less than its ratings buy'
        : row.verdict === 'inline'
          ? 'in line'
          : 'not enough to say';

  return (
    <div
      data-testid={`alignment-${row.cluster}`}
      className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-sm"
    >
      <span className="text-gray-700">{row.cluster}</span>
      <span className="tabular-nums text-gray-500">
        bought <b className="font-medium text-gray-900">{pct(row.expectedShare)}</b> · ran{' '}
        <b className="font-medium text-gray-900">{pct(row.actualShare)}</b>
        {row.interviews != null ? ` (${row.interviews})` : ''} ·{' '}
        <span className={worst ? 'font-medium text-amber-700' : 'text-gray-500'}>{label}</span>
      </span>
    </div>
  );
}
